import isEqual from 'lodash/isEqual';

import { createActionHandler, createReducer, OperationStatus } from '@pressreader/appstore';
import { userChangeDone } from '@pressreader/authentication-core';
import { ArticleAccess, IArticle, type IArticleIssue } from '@pressreader/content-types';
import { ArticleLoadingTags } from '@pressreader/feed-types';

import { mergeArticlesByAvailableFields } from '../../utils/article/merge.articles.by.available.fields';
import { articleAccessGranted, cancelArticlesInIssueTranslationAction, resetArticleTranslation, translateArticleAction } from '../actions';
import {
    articlesLoaded,
    articlesUpdated,
    articleUpdated,
    loadArticleBegan,
    loadArticleCancelled,
    loadArticleDone,
    loadArticleFailed,
} from '../actions/articles';

interface ArticleState {
    article: IArticle | null;
    articleByLanguage: Record<IArticle['language'], IArticle>;
    loading: OperationStatus;
    loadingTags: ArticleLoadingTags[];
    currentLanguage: IArticle['language'] | null;

    /** The load that owns a `Pending` entry — set with `Pending`, cleared by every settle write, so a matching id alone proves the ownership. */
    loadId?: string;
}

interface State {
    articles: Record<string, ArticleState>;
}

const initialState: State = {
    articles: {},
};

function getArticleState(state: Pick<State, 'articles'>, articleId: string): ArticleState {
    return (
        state.articles[articleId] ?? {
            article: null,
            loading: OperationStatus.None,
            loadingTags: [],
            articleByLanguage: {},
        }
    );
}

/** `entry` settled as `loading`: its tags and its load's ownership cleared. */
function settled(entry: ArticleState, loading: OperationStatus): ArticleState {
    return { ...entry, loading, loadingTags: [], loadId: undefined };
}

function isArticleWithOriginalLanguage(article: Pick<IArticle, 'language' | 'originalLanguage'>): boolean {
    return article.language === article.originalLanguage;
}

const isArticleEmpty = (article: Partial<IArticle>): boolean => {
    return article.issue === null && article.paragraphs?.length === 0;
};

/**
 * Merges incoming article data into the entry's version for its language.
 *
 * @returns `null` when the data cannot be stored on the entry at all: it names no language, it is an empty article
 * over an empty entry, or its original language is known neither from the data nor from the stored version (a
 * related-article stub without languages, say).
 */
function updateArticleState({
    currentArticleState,
    articleIncoming,
}: {
    currentArticleState: ArticleState;
    articleIncoming: Partial<IArticle>;
}): ArticleState | null {
    const incomingArticleLanguage = articleIncoming.language;
    if (!incomingArticleLanguage) {
        return null;
    }

    const currentArticle = currentArticleState.article;
    if (isArticleEmpty(articleIncoming) && isArticleEmpty(currentArticle ?? {})) {
        return null;
    }

    const incomingArticleOriginalLanguage = articleIncoming.originalLanguage ?? currentArticle?.originalLanguage;
    if (!incomingArticleOriginalLanguage) {
        return null;
    }

    const isOriginalLanguageArticle = isArticleWithOriginalLanguage({
        language: incomingArticleLanguage,
        originalLanguage: incomingArticleOriginalLanguage,
    });

    let articleToMergeIncomingWith: IArticle | null = null;
    if (isOriginalLanguageArticle) {
        articleToMergeIncomingWith = currentArticleState.article ?? currentArticleState.articleByLanguage[incomingArticleLanguage];
    } else {
        articleToMergeIncomingWith = currentArticleState.articleByLanguage[incomingArticleLanguage];
    }

    const mergedArticle = articleToMergeIncomingWith
        ? mergeArticlesByAvailableFields(articleToMergeIncomingWith, {
              ...articleToMergeIncomingWith,
              ...articleIncoming,
          })
        : (articleIncoming as IArticle);

    if (isEqual(mergedArticle, articleToMergeIncomingWith)) {
        return currentArticleState;
    }

    if (isOriginalLanguageArticle) {
        return {
            ...currentArticleState,
            article: mergedArticle,
        };
    }

    return {
        ...currentArticleState,
        articleByLanguage: {
            ...currentArticleState.articleByLanguage,
            [incomingArticleLanguage]: mergedArticle,
        },
    };
}

function resetToArticleOriginalLanguage(articleState: ArticleState, languageCode?: IArticle['language']): ArticleState {
    const { article: originalLanguageArticle, currentLanguage, articleByLanguage } = articleState;
    const article =
        originalLanguageArticle ??
        (currentLanguage ? articleState.articleByLanguage[currentLanguage] : null) ??
        Object.values(articleByLanguage)[0] ??
        null;
    const articleTargetLanguage = languageCode ?? (article ? article.originalLanguage : null);

    if (!article || !currentLanguage || currentLanguage === articleTargetLanguage) {
        return articleState;
    }
    return {
        ...articleState,
        currentLanguage: articleTargetLanguage,
    };
}

function isArticleFromIssue(issueKey: IArticleIssue['issueKey'], article?: IArticle | null): boolean {
    if (!article?.issue) {
        return false;
    }

    return article.issue.issueKey === issueKey;
}

function isArticleStateBelongsToIssue(issueKey: IArticleIssue['issueKey'], articleState: ArticleState): boolean {
    const { article, articleByLanguage } = articleState;

    if (article?.issue) {
        return isArticleFromIssue(issueKey, article);
    }

    for (const articleByLang of Object.values(articleByLanguage)) {
        if (articleByLang.issue) {
            return isArticleFromIssue(issueKey, articleByLang);
        }
    }

    return false;
}

const reducer = createReducer(
    initialState,
    createActionHandler(userChangeDone, state => ({
        ...state,
        articles: {},
    })),
    createActionHandler(loadArticleBegan, (state, { payload }) => {
        const { articleId, loadingTags, loadId } = payload;

        return {
            ...state,
            articles: {
                ...state.articles,
                [articleId]: {
                    ...getArticleState(state, articleId),
                    articleByLanguage: { ...state.articles[articleId]?.articleByLanguage },
                    loading: OperationStatus.Pending,
                    loadingTags,
                    loadId,
                },
            },
        };
    }),
    createActionHandler(loadArticleDone, (state, { payload }) => {
        const { article: articleIncoming } = payload;
        const articleId = articleIncoming.id;

        const currentArticle = getArticleState(state, articleId).article;
        if (isArticleEmpty(articleIncoming) && currentArticle && currentArticle.issue && currentArticle.paragraphs?.length > 0) {
            return state;
        }

        // Use merge function to intelligently combine with existing article data
        const currentArticleState = getArticleState(state, articleId);
        const updatedArticleState =
            updateArticleState({
                currentArticleState,
                articleIncoming: articleIncoming,
            }) ?? currentArticleState;

        return {
            ...state,
            articles: {
                ...state.articles,
                [articleId]: settled(updatedArticleState, OperationStatus.Done),
            },
        };
    }),
    createActionHandler(loadArticleFailed, (state, { payload }) => {
        const { articleId } = payload;
        const current = getArticleState(state, articleId);
        if (current.loadId !== payload.loadId) {
            return state;
        }

        return {
            ...state,
            articles: {
                ...state.articles,
                [articleId]: settled(current, OperationStatus.Failed),
            },
        };
    }),
    createActionHandler(loadArticleCancelled, (state, { payload }) => {
        const { articleId } = payload;
        const current = getArticleState(state, articleId);
        if (current.loadId !== payload.loadId) {
            return state;
        }

        return {
            ...state,
            articles: {
                ...state.articles,
                [articleId]: settled(
                    current,
                    current.article !== null || Object.keys(current.articleByLanguage).length > 0 ? OperationStatus.Done : OperationStatus.None,
                ),
            },
        };
    }),
    createActionHandler(articleUpdated, (state, { payload }) => {
        const { articleId, article: articleIncoming } = payload;

        const currentArticleState = getArticleState(state, articleId);
        const updatedArticleState = updateArticleState({
            currentArticleState,
            articleIncoming,
        });

        if (updatedArticleState === null || isEqual(updatedArticleState, currentArticleState)) {
            return state;
        }

        return {
            ...state,
            articles: {
                ...state.articles,
                [articleId]: updatedArticleState,
            },
        };
    }),
    createActionHandler(articlesUpdated, (state, { payload }) => {
        const { articleUpdates: articles } = payload;
        const articlesUpdates: typeof state['articles'] = {};

        let isStateUpdated = false;
        for (const articleIncoming of articles) {
            const { articleId } = articleIncoming;
            if (!articleId) {
                continue;
            }

            const currentArticleState = getArticleState(state, articleId);
            const updatedArticleState = updateArticleState({
                currentArticleState,
                articleIncoming,
            });

            if (updatedArticleState === null || isEqual(updatedArticleState, currentArticleState)) {
                continue;
            }

            articlesUpdates[articleId] = updatedArticleState;
            isStateUpdated = true;
        }

        if (!isStateUpdated) {
            return state;
        }
        return {
            ...state,
            articles: {
                ...state.articles,
                ...articlesUpdates,
            },
        };
    }),
    createActionHandler(articleAccessGranted, (state, { payload }) => {
        const { articleId } = payload;
        const articleState = getArticleState(state, articleId);
        const { article } = articleState;
        if (!article) {
            return state;
        }

        return {
            ...state,
            articles: {
                ...state.articles,
                [articleId]: {
                    ...articleState,
                    article: {
                        ...article,
                        access: ArticleAccess.Granted,
                    },
                    articleByLanguage: Object.values(articleState.articleByLanguage).reduce((acc, articleByLang) => {
                        acc[articleByLang.language] = {
                            ...articleByLang,
                            access: ArticleAccess.Granted,
                        };
                        return acc;
                    }, {} as Record<IArticle['language'], IArticle>),
                },
            },
        };
    }),
    createActionHandler(articlesLoaded, (state, { payload }) => {
        const { articles } = payload;
        const stateUpdatedArticles = {
            ...state,
            articles: { ...state.articles },
        };

        let stateChanged = false;
        for (const articleUpdate of articles) {
            const { id: articleId } = articleUpdate;
            const currentArticleState = getArticleState(stateUpdatedArticles, articleId);

            const updatedArticleState = updateArticleState({
                currentArticleState,
                articleIncoming: articleUpdate,
            });

            // Data the entry cannot store is not a load of this article and settles nothing. Accepted data that changes
            // nothing is still a load: a `Pending` entry an overlapping single load marked becomes `Done`, and that load's
            // ownership is retired, so its late failure cannot overwrite this success — only an entry already settled with
            // the same data has nothing to write.
            const isSettled = currentArticleState.loading === OperationStatus.Done;
            if (updatedArticleState === null || (isSettled && isEqual(updatedArticleState, currentArticleState))) {
                continue;
            }

            stateChanged = true;
            stateUpdatedArticles.articles[articleId] = settled(updatedArticleState, OperationStatus.Done);
        }

        return stateChanged ? stateUpdatedArticles : state;
    }),
    createActionHandler(translateArticleAction, (state, { payload }) => {
        const { articleId, languageCode } = payload;
        const articleState = getArticleState(state, articleId);

        return {
            ...state,
            articles: {
                ...state.articles,
                [articleId]: {
                    ...articleState,
                    currentLanguage: languageCode,
                },
            },
        };
    }),
    createActionHandler(resetArticleTranslation, (state, { payload }) => {
        const { articleId, languageCode } = payload;

        const articleState = getArticleState(state, articleId);
        const articleStateWithResetLanguage = resetToArticleOriginalLanguage(articleState, languageCode);

        if (isEqual(articleStateWithResetLanguage, articleState)) {
            return state;
        }

        return {
            ...state,
            articles: {
                ...state.articles,
                [articleId]: articleStateWithResetLanguage,
            },
        };
    }),
    createActionHandler(cancelArticlesInIssueTranslationAction, (state, { payload }) => {
        const { issueKey, languageCode } = payload;
        const updatedArticles: typeof state.articles = {};
        let isStateChanged = false;

        for (const [articleId, articleState] of Object.entries(state.articles)) {
            if (!isArticleStateBelongsToIssue(issueKey, articleState)) {
                continue;
            }

            isStateChanged = true;
            updatedArticles[articleId] = resetToArticleOriginalLanguage(articleState, languageCode);
        }

        if (!isStateChanged) {
            return state;
        }

        return {
            ...state,
            articles: {
                ...state.articles,
                ...updatedArticles,
            },
        };
    }),
);

export { ArticleState, State };
export { reducer };
